import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * This hook handles pending IDs.
 * When calling `addPendingIds` - we can pass whether or not to force adding new IDs or add only missing ones.
 *
 * @typedef {Object} ReturnValue
 * @property {Array.<string>} pendingIdsArray - The array of pending IDs.
 * @property {function(Array.<string>, boolean)} addPendingIds - Add pending IDs. use `force` to make sure their added.
 * @property {function(Array.<string>)} addAlreadyFetchedIds - Add already fetched IDs, to filter future non-force Ids passed to `addPendingIds`.
 * @property {function} clearPendingIds - Clear all pending IDs.
 *
 * @returns {ReturnValue}
 */
export default () => {
  // We use ref to hold the IDs we already fetched, by type and domain. We don't store it in state or reducer as we don't want to trigger re-render when it changes.
  const alreadyFetchedIdsRef = useRef({});
  const [pendingIdsArray, setPendingIdsArray] = useState([]);

  const addAlreadyFetchedIds = useCallback((alreadyFetchedIds) => {
    alreadyFetchedIds.forEach((id) => {
      alreadyFetchedIdsRef.current[id] = true;
    });
  }, []);

  const addPendingIds = useCallback((entityIds, force) => {
    if (entityIds && entityIds.length) {
      setPendingIdsArray((existingPendingIds) => {
        const newPendingIds = force
          ? entityIds
          : entityIds.filter((id) => !alreadyFetchedIdsRef.current[id]);

        if (newPendingIds.length) {
          return [...new Set([...existingPendingIds, ...newPendingIds])];
        }
        return existingPendingIds;
      });
    }
  }, []);

  useEffect(() => {
    addAlreadyFetchedIds(pendingIdsArray);
  }, [pendingIdsArray, addAlreadyFetchedIds]);

  const clearPendingIds = useCallback(() => {
    setPendingIdsArray([]);
  }, []);

  return {
    pendingIdsArray,
    addPendingIds,
    addAlreadyFetchedIds,
    clearPendingIds,
  };
};
